import { useState } from "react";
import { Eye, EyeOff, Pencil, Check, X, Lock } from "lucide-react";
import type UserSetting from "../models/UserSetting";

function isForbidden(e: unknown): boolean {
  return e instanceof Error && (e as Error & { status?: number }).status === 403;
}

interface UserSettingRowProps {
  setting: UserSetting;
  isLast: boolean;
  onGet: (key: number) => Promise<string>;
  onSet: (key: number, newValue: string) => Promise<void>;
}

export function UserSettingRow({
  setting,
  isLast,
  onGet,
  onSet,
}: UserSettingRowProps) {
  const [value, setValue] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [loadingValue, setLoadingValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReveal = async () => {
    if (value === null) {
      setLoadingValue(true);
      setError(null);
      try {
        const result = await onGet(setting.index);
        setValue(result);
      } catch (e) {
        if (isForbidden(e)) {
          // no permission to read — not an error, just stay masked
          setError("No se puede mostrar este valor.");
          setLoadingValue(false);
          return;
        }
        setError("No se pudo cargar el valor.");

        setLoadingValue(false);
        setRevealed((prev) => !prev);
        return;
      }
      setLoadingValue(false);
    }
    setRevealed((prev) => !prev);
  };

  const handleEditClick = async () => {
    if (value === null) {
      setLoadingValue(true);
      setError(null);
      try {
        const result = await onGet(setting.index);
        setValue(result);
        setEditValue(result);
      } catch (e) {
        if (isForbidden(e)) {
          // write-only: allow editing with empty field
          setEditValue("");
          setLoadingValue(false);
          setEditing(true);
          return;
        }
        setError("No se pudo cargar el valor.");
        setLoadingValue(false);
        return;
      }
      setLoadingValue(false);
    } else {
      setEditValue(value);
    }
    setEditing(true);
    setRevealed(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSet(setting.index, editValue);
      setValue(editValue);
      setEditing(false);
    } catch {
      setError("No se pudo guardar el valor.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditValue(value ?? "");
  };

  const displayValue = () => {
    if (loadingValue) return "Cargando…";
    if (error) return error;
    if (value === null || !revealed) return "••••••••";
    return value;
  };

  return (
    <div
      className={`grid grid-cols-[2fr_1fr_3fr_auto] gap-4 px-6 py-5 items-center transition-colors hover:bg-black/2 ${
        !isLast ? "border-b border-black/5" : ""
      }`}
    >
      {/* Key */}
      <span className="font-mono font-semibold text-sm text-foreground">
        {setting.key.includes("_")
          ? setting.key.slice(setting.key.indexOf("_") + 1)
          : setting.key}
      </span>

      {/* Secret badge */}
      <div>
        {setting.isSecret ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100/80 text-amber-700 border border-amber-200/60">
            <Lock className="size-3" />
            Secreto
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100/80 text-emerald-700 border border-emerald-200/60">
            Público
          </span>
        )}
      </div>

      {/* Value / Edit input */}
      <div className="flex items-center gap-2 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            type={setting.isSecret ? "password" : "text"}
            className="flex-1 px-3 py-1.5 rounded-xl border border-black/10 bg-white text-sm text-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all"
          />
        ) : (
          <span
            className={`text-sm font-mono ${
              error
                ? "text-red-500"
                : value === null || !revealed
                  ? "text-foreground/30 tracking-widest"
                  : "text-foreground/70"
            }`}
          >
            {displayValue()}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 ml-auto">
        {editing ? (
          <>
            <button
              onClick={handleSave}
              disabled={saving}
              className="p-2 rounded-xl border border-black/5 bg-white/60 hover:bg-emerald-50/80 hover:border-emerald-200/50 transition-all text-foreground/50 hover:text-emerald-600 disabled:opacity-40"
            >
              <Check className="size-4" />
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="p-2 rounded-xl border border-black/5 bg-white/60 hover:bg-red-50/80 hover:border-red-200/50 transition-all text-foreground/50 hover:text-red-500 disabled:opacity-40"
            >
              <X className="size-4" />
            </button>
          </>
        ) : (
          <>
            {!setting.isSecret && (
              <button
                onClick={handleReveal}
                disabled={loadingValue}
                className="p-2 rounded-xl border border-black/5 bg-white/60 hover:bg-white hover:shadow-sm transition-all text-foreground/50 hover:text-accent disabled:opacity-40"
              >
                {revealed ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            )}

            <button
              onClick={handleEditClick}
              disabled={loadingValue}
              className="p-2 rounded-xl border border-black/5 bg-white/60 hover:bg-white hover:shadow-sm transition-all text-foreground/50 hover:text-accent disabled:opacity-40"
            >
              <Pencil className="size-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
